import React, { useState, useEffect } from "react";
import "./Progress.scss";
import { techStack } from "../../portfolio";

export default function StackProgress() {
  const [animatedSkills, setAnimatedSkills] = useState([]);
  const [activeSkill, setActiveSkill] = useState(0);

  useEffect(() => {
    // Animate skills on load
    const timer = setTimeout(() => {
      setAnimatedSkills(techStack.experience.map((_, index) => index));
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Presentation metadata layered on top of the stacks defined in portfolio.js
  const skillMeta = [
    {
      icon: "📱",
      color: "#0070f3",
      description:
        "Building cross-platform apps in TypeScript that feel native on both iOS and Android, from first screen to store release.",
      level: "Expert",
      years: "5+"
    },
    {
      icon: "🏗️",
      color: "#7c3aed",
      description:
        "Structuring codebases so features can be added without regressions — Redux Toolkit, React Query and clean module boundaries.",
      level: "Advanced",
      years: "4+"
    },
    {
      icon: "⚙️",
      color: "#06b6d4",
      description:
        "Dropping into Swift and Kotlin for video feeds, live broadcasting and camera work that JavaScript alone can't handle.",
      level: "Proficient",
      years: "2+"
    },
    {
      icon: "🚀",
      color: "#10b981",
      description:
        "Owning the path to production: Fastlane, GitHub Actions, code signing, multi-flavour builds and store submissions.",
      level: "Advanced",
      years: "4+"
    }
  ];

  const enhancedSkills = techStack.experience.map((stack, index) => ({
    ...stack,
    ...(skillMeta[index] || skillMeta[skillMeta.length - 1])
  }));

  if (!techStack.viewSkillBars) {
    return null;
  }

  return (
    <div className="proficiency-main-container">
      {/* Interactive Skills Display */}
      <div className="proficiency-content">
        {/* Skills Navigation */}
        <div className="skills-navigation">
          {enhancedSkills.map((skill, index) => (
            <button
              key={index}
              className={`skill-nav-item ${activeSkill === index ? 'active' : ''}`}
              onClick={() => setActiveSkill(index)}
              style={{ '--skill-color': skill.color }}
            >
              <div className="nav-icon">{skill.icon}</div>
              <div className="nav-content">
                <h4 className="nav-title">{skill.Stack}</h4>
                <span className="nav-level">{skill.level}</span>
              </div>
              <div className="nav-percentage">{skill.progressPercentage}</div>
            </button>
          ))}
        </div>

        {/* Active Skill Details */}
        <div className="skill-details-panel">
          <div className="skill-showcase">
            <div className="skill-icon-large" style={{ background: `linear-gradient(135deg, ${enhancedSkills[activeSkill].color}, ${enhancedSkills[activeSkill].color}dd)` }}>
              {enhancedSkills[activeSkill].icon}
            </div>
            
            <div className="skill-info">
              <h3 className="skill-title">{enhancedSkills[activeSkill].Stack}</h3>
              <p className="skill-description">{enhancedSkills[activeSkill].description}</p>
              
              <div className="skill-metrics">
                <div className="metric-item">
                  <div className="metric-label">Experience</div>
                  <div className="metric-value">{enhancedSkills[activeSkill].years} years</div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Proficiency</div>
                  <div className="metric-value">{enhancedSkills[activeSkill].progressPercentage}</div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Level</div>
                  <div className="metric-value">{enhancedSkills[activeSkill].level}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Progress Visualization */}
          <div className="progress-visualization">
            <div className="circular-progress">
              <svg className="progress-ring" width="200" height="200">
                <circle
                  className="progress-ring-background"
                  cx="100"
                  cy="100"
                  r="90"
                />
                <circle
                  className="progress-ring-progress"
                  cx="100"
                  cy="100"
                  r="90"
                  style={{
                    '--progress': parseInt(enhancedSkills[activeSkill].progressPercentage),
                    '--color': enhancedSkills[activeSkill].color
                  }}
                />
              </svg>
              <div className="progress-center">
                <div className="progress-percentage">{enhancedSkills[activeSkill].progressPercentage}</div>
                <div className="progress-label">Proficiency</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skills Comparison Chart */}
      <div className="skills-comparison">
        <h3 className="comparison-title">Skills Overview</h3>
        <div className="comparison-bars">
          {enhancedSkills.map((skill, index) => (
            <div 
              key={index}
              className={`comparison-bar ${animatedSkills.includes(index) ? 'animated' : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="bar-header">
                <div className="bar-info">
                  <span className="bar-icon">{skill.icon}</span>
                  <span className="bar-name">{skill.Stack}</span>
                </div>
                <span className="bar-percentage">{skill.progressPercentage}</span>
              </div>
              <div className="bar-track">
                <div 
                  className="bar-fill"
                  style={{ 
                    '--width': skill.progressPercentage,
                    '--color': skill.color
                  }}
                >
                  <div className="bar-shimmer"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Achievement Stats */}
      <div className="achievement-stats">
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-number">{enhancedSkills.length}</div>
            <div className="stat-label">Core Specializations</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🚀</div>
          <div className="stat-content">
            <div className="stat-number">5+</div>
            <div className="stat-label">Years Experience</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <div className="stat-number">2</div>
            <div className="stat-label">Apps Shipping Now</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🌍</div>
          <div className="stat-content">
            <div className="stat-number">7</div>
            <div className="stat-label">Markets Served</div>
          </div>
        </div>
      </div>
    </div>
  );
}
